/**
 * Écran de jeu `/jeu/:levelId` — **boucle M2 complète, pilotée par le serveur**.
 *
 * Enchaînement d'une run :
 * `POST /runs` → `POST /waves/:i/start` (composition officielle) → simulation Phaser →
 * `POST /waves/:i/result` (faits) → `GET /runs/:id` (état d'autorité) → `POST /cards/options`
 * → choix d'une carte → `POST /cards/choose` → vague suivante → … → `POST /runs/:id/end`.
 *
 * Règles tenues ici, sans exception :
 * - **aucune projection locale** : les éclats, les jokers et les PV affichés viennent de la
 *   dernière réponse serveur (`valeursAffichees`). Le store ne calcule ni prix, ni gain ;
 * - **aucune tour fantôme** : le moteur ne fait que *demander* une pose ; la tour n'apparaît
 *   qu'après `POST /towers` (confirmation serveur) ;
 * - **refus serveur affichés** : chaque échec devient une `ErreurApi`, traduite par
 *   `cleI18nRefus` — solde insuffisant, joker épuisé, emplacement occupé, carte indisponible ;
 * - **run perdue au rechargement** (DECISIONS §7.4) : un marqueur de session contenant seulement
 *   l'identifiant de run permet de détecter un rechargement ; aucune run n'est recréée.
 *
 * Depuis U-01, l'écran de fin propose aussi le **niveau suivant** de la campagne : le numéro
 * validé vient de `POST /runs/:id/end`, le niveau à charger est cherché dans le manifeste publié
 * par son numéro, et le solde persistant est relu (`GET /me`) avant la navigation. Aucun gain n'est
 * recalculé côté client, aucun identifiant de niveau n'est fabriqué.
 */
import { ELEMENTS } from '@elemora/shared/constants';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  ameliorerTourRun,
  chargerManifeste,
  chargerNiveau,
  choisirCarteRun,
  cleI18nErreur,
  cloturerVagueRun,
  composerContenuSimulation,
  creerRun,
  demanderOptionsCartes,
  demarrerVagueRun,
  lireRun,
  placerTourRun,
  recupererProfil,
  terminerRunRun,
  useSessionStore,
  vaguesCouvertes,
  versErreurApi,
  type ErreurApi,
} from '../api';
import { clotureUnRound, roundDepuisIndex, secondesAvantLancement, vagueDuRound } from './illimite';
import { DifficulteManquanteError, resoudreDifficulte } from '../game/logic/difficulte';
import { cleTraductionContenu, formaterNombre } from '../i18n';
import { ajouterCartesObtenues } from '../jeu/effets';
import { ecrireMarqueurRun, lireMarqueurRun } from '../jeu/marqueur-run';
import { numeroVague, prochaineVague, valeursAffichees } from '../jeu/reduction';
import { cleI18nRefus } from '../jeu/refus';
import { reinitialiserRun, useRunStore } from '../jeu/store';
import { useVitesseJeu } from '../jeu/vitesse';
import { Bouton } from '../ui/Bouton';
import { BriefingNiveau } from '../ui/BriefingNiveau';
import { Chargement } from '../ui/Chargement';
import { ConfirmationAbandon } from '../ui/ConfirmationAbandon';
import { EcranFin } from '../ui/EcranFin';
import { EcranFinIllimite } from '../ui/EcranFinIllimite';
import { Entete } from '../ui/Entete';
import { GameCanvas } from '../ui/game-canvas';
import { IconeElement } from '../ui/IconeElement';
import { IconeHud, type CompteurHud } from '../ui/IconeHud';
import { InvitationPaysage } from '../ui/InvitationPaysage';
import { PanneauAmelioration } from '../ui/PanneauAmelioration';
import { PanneauCartes } from '../ui/PanneauCartes';
import { PanneauEffetsRun } from '../ui/PanneauEffetsRun';
import { PanneauFinDeRound } from '../ui/PanneauFinDeRound';
import { PanneauInfosNiveau } from '../ui/PanneauInfosNiveau';
import { SelecteurVitesse } from '../ui/SelecteurVitesse';

import type {
  ControleMoteur,
  PlacementTour,
  ResultatVague,
  TourServeur,
} from '../ui/game-canvas/types';
import type {
  CartePublique,
  ContenuSimulation,
  Manifest,
  NiveauManifeste,
  RunDetail,
  RunState,
} from '@elemora/shared/api';
import type { Niveau, Tour } from '@elemora/shared/content';
import type { CSSProperties } from 'react';

/**
 * Données chargées pour un niveau : contenu publié et identifiant de la run.
 */
interface DonneesJeu {
  readonly manifeste: Manifest;
  readonly niveau: Niveau;
  readonly contenu: ContenuSimulation;
  readonly runId: string;
}

/** Une case du HUD : pictogramme, étiquette et valeur déjà formatée. */
function CaseHud({
  etiquette,
  valeur,
  cle,
}: {
  etiquette: string;
  valeur: string;
  /** Identifiant technique de la case (`data-hud`), pour les mesures et la recette. */
  cle: CompteurHud;
}) {
  return (
    <div className={`hud__case hud__case--${cle}`} data-hud={cle}>
      <dt className="hud__etiquette">
        <IconeHud compteur={cle} />
        <span className="hud__libelle">{etiquette}</span>
      </dt>
      <dd className="hud__valeur">{valeur}</dd>
    </div>
  );
}

/** Convertit une tour d'autorité (`GET /runs/:id`) en tour d'affichage. */
function versTourServeur(tour: RunDetail['towers'][number]): TourServeur {
  return {
    towerId: tour.id,
    tourId: tour.towerId,
    emplacementId: tour.emplacementId,
    niveau: tour.niveau,
  };
}

export function EcranJeu() {
  const { levelId = '' } = useParams<{ levelId: string }>();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const etatRun = useRunStore();
  const vitesseJeu = useVitesseJeu((etat) => etat.vitesse);
  const definirVitesseJeu = useVitesseJeu((etat) => etat.definirVitesse);
  const valeurs = valeursAffichees(etatRun);

  const [etatChargement, setEtatChargement] = useState<'chargement' | 'pret' | 'erreur'>(
    'chargement',
  );
  const [erreurChargement, setErreurChargement] = useState<ErreurApi | null>(null);
  const [donnees, setDonnees] = useState<DonneesJeu | null>(null);
  const [tentative, setTentative] = useState(0);
  const [moteurPret, setMoteurPret] = useState(false);
  const [messageLocal, setMessageLocal] = useState<string | null>(null);

  /**
   * Fin de round du **mode illimité** (A-25, §7.6).
   *
   * Le montant versé et le solde de run sont recopiés de la réponse serveur à la clôture de la
   * vague : aucune addition locale, aucun bonus supposé connu du client.
   */
  const [finDeRound, setFinDeRound] = useState<{
    readonly roundTermine: number;
    readonly roundSuivant: number;
    readonly versement: number;
    readonly eclatsRun: number;
  } | null>(null);
  /** Confirmation d'abandon demandée (le bouton n'agit jamais du premier coup). */
  const [abandonDemande, setAbandonDemande] = useState(false);
  /** Secondes restantes avant le lancement forcé d'une vague (mode illimité, §7 pt 7). */
  const [secondesRestantes, setSecondesRestantes] = useState<number | null>(null);

  /**
   * Panneau **« Effets de la run »** (U-03), consultable pendant la partie.
   *
   * Ouvert, il ne met **pas** la vague en pause (décision de la carte) : le panneau le rappelle au
   * joueur, et le serveur reste seul juge de ce que la vague produit pendant la consultation.
   */
  const [panneauEffetsOuvert, setPanneauEffetsOuvert] = useState(false);
  /** Tiroir « Infos du niveau » (briefing et aide), ouvert depuis la barre du haut. */
  const [panneauInfosOuvert, setPanneauInfosOuvert] = useState(false);
  /**
   * Cartes accordées par le serveur **pendant cette run**, dans l'ordre d'obtention.
   *
   * Alimenté exclusivement par les réponses de `POST /runs/:id/cards/choose` (`card` et
   * `bonusCard`) : aucune carte n'est déduite d'un catalogue local, et aucune valeur d'effet n'est
   * recalculée. Les jokers et les multiplicateurs affichés à côté, eux, viennent de l'état
   * d'autorité (`GET /runs/:id`), pas de cette liste.
   */
  const [cartesObtenuesRun, setCartesObtenuesRun] = useState<readonly CartePublique[]>([]);
  /** Bouton d'ouverture du panneau : le focus y revient à la fermeture. */
  const boutonEffets = useRef<HTMLButtonElement>(null);
  const boutonInfos = useRef<HTMLButtonElement>(null);

  const moteurRef = useRef<ControleMoteur | null>(null);
  const chargementRef = useRef<{ cle: string; promesse: Promise<DonneesJeu> } | null>(null);
  /** Verrou d'action : une seule requête de jeu à la fois (double clic, appui répété). */
  const actionEnCours = useRef(false);
  const clotureEnCours = useRef(false);
  const defaiteEnCours = useRef(false);

  /**
   * Détection d'un rechargement en pleine run, calculée **une seule fois** par montage.
   *
   * `useRef` avec lecture paresseuse : React StrictMode rend le composant deux fois et une seconde
   * lecture verrait le marqueur déjà retiré (faux négatif). Le ref survit au double rendu.
   */
  const marqueurInitial = useRef<string | null | undefined>(undefined);
  if (marqueurInitial.current === undefined) marqueurInitial.current = lireMarqueurRun();
  const runPerdueDetectee = marqueurInitial.current !== null;

  /**
   * Mode de la run et **taille d'un round**, lues dans le contenu publié : le gabarit
   * `niv_illimite` fait foi (8 vagues), aucune constante n'est écrite en dur ici. La pause forcée
   * du mode illimité vient également de `balance` (§7 pt 7).
   */
  const illimite = donnees?.niveau.mode === 'illimite';
  const vaguesParRound = donnees?.niveau.vagues.length ?? 0;
  const pauseSecondes = secondesAvantLancement(donnees?.manifeste.balance.pause_illimite_ms ?? 0);

  /**
   * Niveau suivant de la **campagne** (U-01), déduit de deux valeurs d'autorité :
   * - le numéro validé, renvoyé par `POST /runs/:id/end` (`niveauAtteint`) ;
   * - la liste des niveaux du manifeste publié (`levels` : id, numéro, mode).
   *
   * Aucun identifiant n'est fabriqué : si le numéro attendu n'est pas publié, `niveauSuivant`
   * reste `null` et l'écran de fin le dit. Le mode illimité est exclu : sa progression est
   * interne à la run (rounds de 8 vagues, §7.6), il n'y a pas de niveau suivant à charger.
   */
  const finRun = etatRun.finRun;
  const niveauValide =
    finRun !== null && finRun.raison === 'victoire' ? finRun.reponse.niveauAtteint : 0;
  const niveauxPublies: readonly NiveauManifeste[] = donnees?.manifeste.levels ?? [];
  const niveauxCampagne = niveauxPublies.filter((candidat) => candidat.mode === 'limite');
  const niveauSuivant =
    niveauValide > 0
      ? (niveauxCampagne.find((candidat) => candidat.numero === niveauValide + 1) ?? null)
      : null;
  /** Un numéro plus haut que celui attendu est publié : le contenu a un trou, on le signale. */
  const niveauPlusHautExiste = niveauxCampagne.some(
    (candidat) => candidat.numero > niveauValide + 1,
  );
  const campagneTerminee = niveauValide > 0 && niveauSuivant === null && !niveauPlusHautExiste;
  const niveauSuivantIndisponible =
    niveauValide > 0 && niveauSuivant === null && niveauPlusHautExiste;

  /** État de la transition « Niveau suivant » (U-01) : inactif, en cours, dernier essai échoué. */
  const [transitionNiveau, setTransitionNiveau] = useState<'inactive' | 'en-cours' | 'echec'>(
    'inactive',
  );
  /** Verrou d'action de la transition : un seul appel réseau à la fois (double appui). */
  const transitionNiveauEnCours = useRef(false);

  /**
   * Charge le niveau, le manifeste, puis crée la run.
   *
   * Trois points non triviaux :
   * 1. la promesse est **partagée** par clé : StrictMode exécute l'effet deux fois et deux appels
   *    à `POST /runs` créeraient deux runs (la seconde resterait active côté serveur) ;
   * 2. `contentVersion` est **vérifiée** : si le serveur a publié une autre version entre le
   *    manifeste et la run, le contenu est rechargé une fois (`RUN_VERSION_MISMATCH` sur `409`) ;
   * 3. une erreur `401` n'est pas affichée : la garde d'accès redirige vers `/connexion`.
   */
  const chargerDonnees = useCallback(
    (cle: string): Promise<DonneesJeu> => {
      const existante = chargementRef.current;
      if (existante !== null && existante.cle === cle) return existante.promesse;

      const promesse = (async (): Promise<DonneesJeu> => {
        let manifeste = await chargerManifeste();
        const niveau = await chargerNiveau(levelId);

        for (let essai = 0; essai < 2; essai += 1) {
          try {
            const creation = await creerRun({
              mode: niveau.mode,
              levelId,
              contentVersion: manifeste.contentVersion,
            });
            // Le serveur fait autorité : si sa version diffère de celle du manifeste lu
            // (publication pendant le chargement), on recharge le contenu et on redemande une run.
            if (creation.state.contentVersion !== manifeste.contentVersion && essai === 0) {
              manifeste = await chargerManifeste();
              continue;
            }
            const contenu = composerContenuSimulation(manifeste, niveau);
            return { manifeste, niveau, contenu, runId: creation.runId };
          } catch (erreur) {
            const normalisee = versErreurApi(erreur);
            if (normalisee.code === 'RUN_VERSION_MISMATCH' && essai === 0) {
              manifeste = await chargerManifeste();
              continue;
            }
            throw normalisee;
          }
        }
        // Deux échecs de version d'affilée : le contenu publié bouge en boucle, on s'arrête là.
        throw versErreurApi(new Error('Version de contenu instable pendant le chargement.'));
      })();

      chargementRef.current = { cle, promesse };
      return promesse;
    },
    [levelId],
  );

  // Rechargement en pleine run : on signale la perte une seule fois, avant tout appel réseau.
  useEffect(() => {
    if (!runPerdueDetectee) return;
    ecrireMarqueurRun(null);
    useRunStore.getState().envoyer({ type: 'run-perdue', raison: 'rechargement' });
  }, [runPerdueDetectee]);

  useEffect(() => {
    let actif = true;

    // Run perdue au rechargement : on n'en recrée pas une en silence, l'écran explique §7.4.
    if (runPerdueDetectee) {
      setEtatChargement('pret');
      return () => {
        actif = false;
      };
    }

    setEtatChargement('chargement');
    setErreurChargement(null);
    chargerDonnees(`${levelId}#${tentative}`)
      .then((chargees) => {
        if (!actif) return;
        setDonnees(chargees);
        setEtatChargement('pret');
        ecrireMarqueurRun(chargees.runId);
        setEtatChargement('pret');
      })
      .catch((erreur: unknown) => {
        if (!actif) return;
        const normalisee = versErreurApi(erreur);
        // `401` : la session est purgée par le client HTTP, la garde redirige. Rien à afficher.
        if (normalisee.statutHttp === 401) return;
        setErreurChargement(normalisee);
        setEtatChargement('erreur');
      });

    return () => {
      actif = false;
    };
  }, [chargerDonnees, levelId, tentative, runPerdueDetectee]);

  /**
   * Après la création de la run : l'état d'autorité complet (`GET /runs/:id`) alimente le store.
   *
   * Une seule lecture sert de source à `serveur`, `toursPosees` et `vagueIndex` : aucune valeur
   * n'est déduite du contenu local.
   */
  const initialiserRun = useCallback((chargees: DonneesJeu) => {
    void lireRun(chargees.runId)
      .then((detail) => {
        useRunStore.getState().envoyer({
          type: 'run-prete',
          runId: detail.id,
          run: detail.state,
          tours: detail.towers.map(versTourServeur),
          vagueIndex: prochaineVague(detail.waves),
        });
      })
      .catch((erreur: unknown) => {
        const normalisee = versErreurApi(erreur);
        if (normalisee.statutHttp === 401) return;
        setErreurChargement(normalisee);
        setEtatChargement('erreur');
      });
  }, []);

  useEffect(() => {
    if (donnees === null || runPerdueDetectee) return;
    initialiserRun(donnees);
  }, [donnees, initialiserRun, runPerdueDetectee]);

  // Sortie de l'écran : la run est abandonnée (aucune reprise, DECISIONS §7.4).
  useEffect(() => {
    return () => {
      ecrireMarqueurRun(null);
      setMoteurPret(false);
      moteurRef.current = null;
      reinitialiserRun();
    };
  }, []);

  /** Exécute une action serveur en verrouillant l'interface, puis relâche le verrou. */
  const executer = useCallback(async (action: () => Promise<void>): Promise<void> => {
    if (actionEnCours.current) return;
    actionEnCours.current = true;
    useRunStore.getState().envoyer({ type: 'action-debut' });
    try {
      await action();
    } catch (erreur) {
      const normalisee = versErreurApi(erreur);
      if (normalisee.statutHttp !== 401) {
        useRunStore.getState().envoyer({ type: 'refus', erreur: normalisee });
      }
    } finally {
      actionEnCours.current = false;
      useRunStore.getState().envoyer({ type: 'action-fin' });
    }
  }, []);

  /**
   * Fin de run : `victoire` (gains validés) ou `defaite` (solde perdu).
   *
   * La réponse du serveur (`eclatsValides`, `eclatsCredites`, `niveauAtteint`) est la seule source
   * affichée. Le marqueur de session est retiré : une run close n'a plus à être « reprise ».
   */
  const terminerRun = useCallback(
    async (raison: 'victoire' | 'defaite' | 'abandon'): Promise<void> => {
      const courant = useRunStore.getState();
      if (courant.runId === null) return;
      const reponse = await terminerRunRun(courant.runId, { reason: raison });
      ecrireMarqueurRun(null);
      useRunStore.getState().envoyer({ type: 'fin-run', raison, reponse });
    },
    [],
  );

  /**
   * Passage au niveau suivant (U-01).
   *
   * Trois règles, dans cet ordre :
   * 1. **les gains sont déjà persistés** : `POST /runs/:id/end {reason:'victoire'}` a versé le
   *    solde de la run et relevé `progressions.niveau_max` dans la même transaction (runs.service) ;
   * 2. **le solde affiché ensuite vient du serveur** : on relit `GET /me` et on recopie sa
   *    progression — jamais `éclats validés + ancien solde` recalculé ici ;
   * 3. **aucune transition locale inventée** : la navigation change seulement de niveau, et
   *    l'écran de jeu (remonté par la clé de route) redemande au serveur une run pour ce niveau.
   *
   * En cas d'échec réseau, on **ne quitte pas** l'écran de fin : les gains sont enregistrés, le
   * message le dit et le bouton reste disponible pour réessayer. Un `401` n'affiche rien : la
   * garde d'accès redirige vers la connexion.
   */
  const passerAuNiveauSuivant = useCallback(() => {
    if (niveauSuivant === null || transitionNiveauEnCours.current) return;
    transitionNiveauEnCours.current = true;
    setTransitionNiveau('en-cours');

    void (async () => {
      try {
        const profil = await recupererProfil();
        useSessionStore.getState().definirProgression(profil.progression);
        // La run validée est close : le marqueur de session ne doit pas faire croire à un
        // rechargement quand le nouvel écran de jeu se monte.
        ecrireMarqueurRun(null);
        setTransitionNiveau('inactive');
        navigate(`/jeu/${niveauSuivant.id}`);
      } catch (erreur) {
        const normalisee = versErreurApi(erreur);
        if (normalisee.statutHttp === 401) return;
        setTransitionNiveau('echec');
      } finally {
        transitionNiveauEnCours.current = false;
      }
    })();
  }, [niveauSuivant, navigate]);

  /**
   * Pose d'une tour : le moteur a émis une **intention**, l'écran demande au serveur.
   *
   * `jokerConsomme` reflète la phase côté client (fait de jeu : la pose a lieu pendant une vague),
   * mais c'est le serveur qui vérifie le joker et débite les éclats. La tour n'est ajoutée à la
   * simulation qu'après confirmation : un refus ne laisse donc aucune tour fantôme.
   */
  const poserTour = useCallback(
    (placement: PlacementTour) => {
      const courant = useRunStore.getState();
      if (courant.runId === null || donnees === null) return;
      const pendantVague = courant.phase === 'vague';

      void executer(async () => {
        const reponse = await placerTourRun(courant.runId as string, {
          towerId: placement.tourId,
          emplacementId: placement.emplacementId,
          jokerConsomme: pendantVague,
        });
        const detail = await lireRun(courant.runId as string);
        const tour: TourServeur = {
          towerId: reponse.tower.id,
          tourId: reponse.tower.towerId,
          emplacementId: reponse.tower.emplacementId,
          niveau: reponse.tower.niveau,
        };
        useRunStore.getState().envoyer({ type: 'pose-confirmee', tour, run: detail.state });
        moteurRef.current?.confirmerPose(tour);
        setMessageLocal(null);
      });
    },
    [donnees, executer],
  );

  /** Amélioration d'une tour posée : corps vide, niveau et débit décidés par le serveur. */
  const ameliorerTour = useCallback(() => {
    const courant = useRunStore.getState();
    const tour = courant.tourAmelioration;
    if (courant.runId === null || tour === null) return;

    void executer(async () => {
      const reponse = await ameliorerTourRun(courant.runId as string, tour.towerId);
      const detail = await lireRun(courant.runId as string);
      const miseAJour: TourServeur = { ...tour, niveau: reponse.tower.niveau };
      useRunStore
        .getState()
        .envoyer({ type: 'amelioration-confirmee', tour: miseAJour, run: detail.state });
      moteurRef.current?.confirmerAmelioration(miseAJour.towerId, miseAJour.niveau);
      setMessageLocal(null);
    });
  }, [executer]);

  /**
   * Lancement manuel d'une vague : le serveur fournit la composition officielle **et** la
   * difficulté à appliquer (`wave.difficulte`, §7.6 / A-25).
   *
   * Le facteur est transporté **tel quel** jusqu'à la simulation : le client ne le recalcule
   * jamais depuis `balance` — le serveur est la source de vérité, et le facteur qu'il publie porte
   * déjà le cumul des rounds (A-25, A-35).
   *
   * En mode illimité au round ≥ 2, une composition sans difficulté est **refusée** (erreur
   * nommée) : lancer la vague avec les statistiques du round précédent serait exactement le défaut
   * D-M3-05. Le message est local — aucun repli silencieux, aucun appel serveur supplémentaire.
   */
  const lancerVague = useCallback(() => {
    const courant = useRunStore.getState();
    if (courant.runId === null || courant.phase !== 'prete' || !moteurPret) return;
    const index = courant.vagueIndex;

    void executer(async () => {
      const { wave } = await demarrerVagueRun(courant.runId as string, index);

      let difficulte: number;
      try {
        difficulte = resoudreDifficulte(wave, {
          illimite,
          round: roundDepuisIndex(index, vaguesParRound),
          indexVague: index,
        });
      } catch (erreur) {
        if (erreur instanceof DifficulteManquanteError) {
          setMessageLocal(t('jeu.difficulteManquante'));
          return;
        }
        throw erreur;
      }

      const lancee = moteurRef.current?.lancerVague(wave.spawns, difficulte) ?? false;
      if (!lancee) {
        throw versErreurApi(new Error('Le moteur n’a pas pu lancer la vague.'));
      }
      useRunStore.getState().envoyer({ type: 'vague-demarree', index });
      setMessageLocal(null);
    });
  }, [executer, illimite, moteurPret, t, vaguesParRound]);

  /**
   * Clôture d'une vague : faits envoyés au serveur, gains recalculés, puis choix de carte.
   *
   * Aucun montant n'est calculé ici : `eclatsGagnes` et `eclatsRun` viennent de la réponse. Les
   * PV à 0 sont détectés sur l'état d'autorité rafraîchi (le serveur a déjà clos la run).
   */
  const cloturerVague = useCallback(
    (resultat: ResultatVague) => {
      const courant = useRunStore.getState();
      const runId = courant.runId;
      if (runId === null || clotureEnCours.current) return;
      clotureEnCours.current = true;
      useRunStore.getState().envoyer({ type: 'vague-close', faits: resultat });

      void (async () => {
        try {
          const reponse = await cloturerVagueRun(runId, resultat.index, {
            ennemisTues: [...resultat.ennemisTues],
            fuites: [...resultat.fuites],
            pvParcoursRestant: resultat.pvParcoursRestant,
          });
          const detail = await lireRun(runId);
          useRunStore.getState().envoyer({
            type: 'vague-validee',
            eclatsGagnes: reponse.eclatsGagnes,
            eclatsRun: reponse.eclatsRun,
            vagueSuivante: reponse.waveSuivante?.index ?? null,
            run: detail.state,
          });

          // Fin de round du mode illimité : le serveur a versé le solde du round et ouvert le
          // suivant. Le montant affiché est **sa** valeur (`versement`), jamais un calcul local.
          // L'écran prend le pas sur le choix de carte, qui reste disponible juste après.
          if (illimite && clotureUnRound(resultat.index, vaguesParRound)) {
            setFinDeRound({
              roundTermine: roundDepuisIndex(resultat.index, vaguesParRound),
              roundSuivant: roundDepuisIndex(resultat.index + 1, vaguesParRound),
              versement: reponse.versement ?? 0,
              eclatsRun: reponse.eclatsRun,
            });
          }

          if (detail.state.pvParcours <= 0) {
            if (!defaiteEnCours.current) {
              defaiteEnCours.current = true;
              await terminerRun('defaite');
            }
            return;
          }
          if (reponse.waveSuivante === null) {
            await terminerRun('victoire');
            return;
          }

          const options = await demanderOptionsCartes(runId, { waveIndex: resultat.index });
          useRunStore.getState().envoyer({
            type: 'options-cartes',
            options: options.options,
            bonusCardId: options.bonusCardId,
          });
        } catch (erreur) {
          const normalisee = versErreurApi(erreur);
          if (normalisee.statutHttp !== 401) {
            useRunStore.getState().envoyer({ type: 'refus', erreur: normalisee });
          }
        } finally {
          clotureEnCours.current = false;
        }
      })();
    },
    [terminerRun, illimite, vaguesParRound],
  );

  /** Choix d'une carte parmi les trois proposées : le serveur débloque et met à jour le deck. */
  const choisirCarte = useCallback(
    (cardId: string) => {
      const courant = useRunStore.getState();
      const runId = courant.runId;
      const vagueIndex = courant.derniereVague?.faits.index;
      if (runId === null || vagueIndex === undefined) return;

      void executer(async () => {
        const reponse = await choisirCarteRun(runId, { waveIndex: vagueIndex, cardId });
        // Les deux cartes que le serveur vient d'accorder (choisie et bonus éventuelle) rejoignent
        // la liste du panneau « Effets de la run » : ce sont ses définitions publiées, pas celles
        // du manifeste lu avant la run (qui ne connaissait pas encore l'effet d'une carte neuve).
        setCartesObtenuesRun((precedentes) =>
          ajouterCartesObtenues(precedentes, [reponse.card, reponse.bonusCard]),
        );
        const detail = await lireRun(runId);
        useRunStore.getState().envoyer({ type: 'carte-choisie', run: detail.state });
        setMessageLocal(null);
      });
    },
    [executer],
  );

  /**
   * Fermeture du panneau : le focus revient au bouton qui l'a ouvert, pour ne pas laisser le
   * joueur au clavier sur un élément qui vient de disparaître.
   */
  const fermerPanneauInfos = useCallback(() => {
    setPanneauInfosOuvert(false);
    boutonInfos.current?.focus();
  }, []);

  const fermerPanneauEffets = useCallback(() => {
    setPanneauEffetsOuvert(false);
    boutonEffets.current?.focus();
  }, []);

  /** PV à 0 pendant une vague : la run est perdue, le serveur confirme et clôt. */
  const surDefaite = useCallback(() => {
    if (defaiteEnCours.current) return;
    defaiteEnCours.current = true;
    void executer(async () => {
      await terminerRun('defaite');
    });
  }, [executer, terminerRun]);

  /**
   * Abandon confirmé (§7.6) : le serveur clôt la run en `perdue` et le solde du round en cours
   * n'est pas versé. Aucun montant n'est calculé ici — l'écran de fin affiche la réponse serveur.
   */
  const abandonnerRun = useCallback(() => {
    const courant = useRunStore.getState();
    if (courant.runId === null) return;
    void executer(async () => {
      await terminerRun('abandon');
      setAbandonDemande(false);
    });
  }, [executer, terminerRun]);

  /**
   * Compte à rebours du mode illimité (§7 pt 7) : « lancement manuel dans les deux modes, mais un
   * compte à rebours de 30 s force le lancement pour empêcher les runs infinies ».
   *
   * La durée vient de `balance.pause_illimite_ms` (contenu publié). Le décompte ne tourne que
   * pendant une phase où le lancement est possible : ni pendant une vague, ni avant l'accord du
   * serveur, ni pendant une confirmation d'abandon ou un versement de fin de round.
   */
  useEffect(() => {
    if (!illimite || pauseSecondes <= 0 || !moteurPret) {
      setSecondesRestantes(null);
      return;
    }
    if (
      etatRun.phase !== 'prete' ||
      etatRun.actionEnCours ||
      abandonDemande ||
      finDeRound !== null
    ) {
      setSecondesRestantes(null);
      return;
    }
    setSecondesRestantes(pauseSecondes);
    const minuterie = window.setInterval(() => {
      setSecondesRestantes((restantes) => (restantes === null ? null : Math.max(0, restantes - 1)));
    }, 1000);
    return () => {
      window.clearInterval(minuterie);
    };
  }, [
    illimite,
    pauseSecondes,
    moteurPret,
    etatRun.phase,
    etatRun.actionEnCours,
    abandonDemande,
    finDeRound,
  ]);

  /** Compte arrivé à zéro : la vague suivante part sans action du joueur. */
  useEffect(() => {
    if (secondesRestantes !== 0) return;
    setSecondesRestantes(null);
    lancerVague();
  }, [secondesRestantes, lancerVague]);

  if (etatChargement === 'chargement') {
    return (
      <div className="ecran ecran--jeu">
        <Entete avecDeconnexion />
        <Chargement texte={t('jeu.chargement')} />
      </div>
    );
  }

  if (etatChargement === 'erreur' && erreurChargement !== null) {
    // Un identifiant de niveau inconnu renvoie `VALIDATION_FAILED` + `details.ressource = 'level'`
    // (point ouvert M1-02) : on affiche alors un message dédié plutôt qu'une erreur de validation.
    const niveauIntrouvable = erreurChargement.details?.['ressource'] === 'level';
    return (
      <div className="ecran ecran--jeu">
        <Entete avecDeconnexion />
        <main className="panneau message-erreur" role="alert">
          <p>{niveauIntrouvable ? t('jeu.niveauInconnu') : t(cleI18nErreur(erreurChargement))}</p>
          <div className="message-erreur__actions">
            <Bouton
              variante="secondaire"
              onClick={() => {
                setTentative((valeur) => valeur + 1);
              }}
            >
              {t('commun.reessayer')}
            </Bouton>
            <Link to="/">{t('commun.retourAccueil')}</Link>
          </div>
        </main>
      </div>
    );
  }

  if (etatRun.phase === 'perdue') {
    return (
      <div className="ecran ecran--jeu">
        <Entete avecDeconnexion />
        <main className="panneau message-erreur" role="alert" data-run-perdue="oui">
          <h1 className="panneau__titre">{t('jeu.runPerdue.titre')}</h1>
          <p>{t('jeu.runPerdue.texte')}</p>
          <div className="message-erreur__actions">
            <Bouton variante="principal" onClick={() => navigate('/')}>
              {t('commun.retourAccueil')}
            </Bouton>
          </div>
        </main>
      </div>
    );
  }

  if (donnees === null || etatRun.serveur === null) {
    return (
      <div className="ecran ecran--jeu">
        <Entete avecDeconnexion />
        <Chargement texte={t('jeu.chargement')} />
      </div>
    );
  }

  const { niveau, contenu, runId } = donnees;

  // Fin de run : l'écran de fin remplace le plateau (les valeurs viennent de `POST /end`).
  // Le mode illimité a son propre écran : il n'y a pas de « victoire », mais un motif (défaite ou
  // abandon) et un nombre de rounds réussis (§7.6).
  if (etatRun.finRun !== null) {
    return (
      <div
        className="ecran ecran--jeu"
        data-phase={etatRun.phase}
        data-run-id={runId}
        data-mode={niveau.mode}
      >
        <Entete avecDeconnexion />
        <main className="zone-jeu">
          {niveau.mode === 'illimite' ? (
            <EcranFinIllimite
              fin={etatRun.finRun}
              onRetourAccueil={() => {
                navigate('/');
              }}
            />
          ) : (
            <EcranFin
              fin={etatRun.finRun}
              vagueAtteinte={Math.min(numeroVague(etatRun), niveau.vagues.length)}
              totalVagues={niveau.vagues.length}
              toursPosees={etatRun.toursPosees.length}
              jokersRestants={valeurs.jokers}
              onNiveauSuivant={niveauSuivant === null ? undefined : passerAuNiveauSuivant}
              transitionEnCours={transitionNiveau === 'en-cours'}
              transitionEchouee={transitionNiveau === 'echec'}
              campagneTerminee={campagneTerminee}
              niveauSuivantIndisponible={niveauSuivantIndisponible}
              onRetourAccueil={() => {
                navigate('/');
              }}
            />
          )}
        </main>
      </div>
    );
  }

  const contenuComplet = vaguesCouvertes(donnees.manifeste, niveau.vagues);
  const nomNiveau = t(cleTraductionContenu(niveau.nameKey));
  const titreNiveau = t('jeu.titre', {
    numero: formaterNombre(niveau.numero, i18n.language),
    nom: nomNiveau,
  });
  const tours = ELEMENTS.map((element) =>
    contenu.tours.find((candidate) => candidate.element === element),
  ).filter((candidate): candidate is Tour => candidate !== undefined);
  const vagueAffichee = numeroVague(etatRun);
  /**
   * Vue du **mode illimité** : l'index de vague est global (0…7 = round 1, 8…15 = round 2, …), le
   * serveur ne stocke aucun numéro de round (§7.6, A-25). Ces deux valeurs sont une simple lecture
   * de l'index, jamais une donnée envoyée au serveur.
   */
  const roundAffiche = illimite ? roundDepuisIndex(etatRun.vagueIndex, vaguesParRound) : null;
  const vagueDuRoundAffichee = illimite ? vagueDuRound(etatRun.vagueIndex, vaguesParRound) : null;
  const enCours = etatRun.phase === 'vague' || etatRun.phase === 'cloture';
  const lancementPossible = moteurPret && etatRun.phase === 'prete' && !etatRun.actionEnCours;
  const refus = etatRun.refus;
  const messageRefus =
    refus === null
      ? messageLocal
      : cleI18nRefus(refus) === null
        ? null
        : t(cleI18nRefus(refus) as string);
  const definitionAmelioration =
    etatRun.tourAmelioration === null
      ? undefined
      : contenu.tours.find((tour) => tour.id === etatRun.tourAmelioration?.tourId);

  // État transmis au canvas : **exactement** les valeurs affichées par le HUD.
  const etatPourMoteur: RunState = {
    ...etatRun.serveur,
    eclatsRun: valeurs.eclats,
    jokers: valeurs.jokers,
    pvParcours: valeurs.pv,
  };

  return (
    <div
      className="ecran ecran--jeu ecran--partie"
      data-run-id={runId}
      data-moteur-pret={moteurPret ? 'oui' : 'non'}
      data-phase={etatRun.phase}
      data-pv-source={valeurs.sourcePv}
      data-tours-posees={etatRun.toursPosees.length}
      data-vague-index={etatRun.vagueIndex}
      data-options-cartes={etatRun.optionsCartes?.length ?? 0}
      data-version-contenu={etatRun.contentVersion ?? ''}
      data-mode={niveau.mode}
      data-round={roundAffiche ?? undefined}
      data-vague-du-round={vagueDuRoundAffichee ?? undefined}
      data-round-termine={finDeRound?.roundTermine ?? undefined}
      data-cartes-obtenues={cartesObtenuesRun.length}
    >
      <Entete avecDeconnexion />
      {/*
        Barre du haut de l'écran de jeu : titre du niveau, infos (briefing et aide) et effets de la
        run. Les compteurs (PV, éclats, vague, jokers) sont en bas, dans le dock, à côté des tours :
        tout ce qui se joue est sous le pouce.
      */}
      <header className="jeu-entete">
        <div className="hud__haut">
          <h1 className="hud__titre">{titreNiveau}</h1>
          <button
            type="button"
            className="hud__infos"
            ref={boutonInfos}
            data-ouvrir-infos="oui"
            aria-haspopup="dialog"
            aria-expanded={panneauInfosOuvert}
            aria-label={t('jeu.infos.ouvrir')}
            onClick={() => {
              // Un seul panneau à la fois : la fiche d'amélioration (feuille basse sur mobile)
              // passerait sinon par-dessus le tiroir.
              if (useRunStore.getState().tourAmelioration !== null) {
                useRunStore.getState().envoyer({ type: 'selection-tour-posee', tour: null });
              }
              setPanneauInfosOuvert(true);
            }}
          >
            <span className="hud__infos-icone" aria-hidden="true">
              i
            </span>
            <span className="hud__infos-libelle">{t('jeu.infos.bouton')}</span>
          </button>
          {/*
            Accès permanent au panneau « Effets de la run » (U-03). Le compteur est celui des
            cartes accordées par le serveur dans cette run ; le panneau, lui, lit les jokers et les
            multiplicateurs dans l'état d'autorité.
          */}
          <button
            type="button"
            className="hud__effets"
            ref={boutonEffets}
            data-ouvrir-effets={cartesObtenuesRun.length}
            aria-haspopup="dialog"
            aria-expanded={panneauEffetsOuvert}
            aria-label={t('jeu.effets.ouvrir', {
              nombre: formaterNombre(cartesObtenuesRun.length, i18n.language),
            })}
            onClick={() => {
              setPanneauEffetsOuvert(true);
            }}
          >
            <span className="hud__effets-libelle">{t('jeu.effets.bouton')}</span>
            <span className="hud__effets-compteur" aria-hidden="true">
              {formaterNombre(cartesObtenuesRun.length, i18n.language)}
            </span>
          </button>
        </div>
      </header>

      <main className="zone-jeu">
        {/*
          Le plateau et tout ce qui se pose **dessus** : invitation au paysage, messages de refus
          ou d'attente (en bandeau, sans pousser le plateau) et panneau d'amélioration d'une tour.
        */}
        <div className="plateau-jeu">
          {contenuComplet ? (
            <GameCanvas
              levelId={levelId}
              contenu={contenu}
              etatRun={etatPourMoteur}
              tourSelectionnee={etatRun.tourSelectionnee}
              vitesse={vitesseJeu}
              onPoseDemandee={poserTour}
              onTourSelectionnee={(tour) => {
                useRunStore.getState().envoyer({ type: 'selection-tour-posee', tour });
              }}
              onVagueTerminee={cloturerVague}
              onPvPerdus={(pv) => {
                useRunStore.getState().envoyer({ type: 'pv-observes', pv });
              }}
              onPartiePerdue={surDefaite}
              onPret={() => {
                setMoteurPret(true);
              }}
              onTourRefusee={(raison) => {
                setMessageLocal(
                  t(`jeu.refus.${raison}`, { defaultValue: t('jeu.refus.inconnue') }),
                );
              }}
              surMoteur={(moteur) => {
                moteurRef.current = moteur;
                if (moteur === null) setMoteurPret(false);
              }}
            />
          ) : (
            <p className="panneau message-erreur" role="alert" data-contenu-incomplet="oui">
              {t('jeu.contenuIncomplet')}
            </p>
          )}

          <div className="plateau-jeu__bandeaux">
            <InvitationPaysage />
            {messageRefus === null ? null : (
              <p className="note note--alerte" role="status" data-refus="oui">
                {messageRefus}
              </p>
            )}
            {etatRun.phase === 'cloture' ? (
              <p className="note note--attente" role="status" data-attente-serveur="oui">
                {t('jeu.clotureEnCours')}
              </p>
            ) : null}
          </div>

          {etatRun.tourAmelioration === null || definitionAmelioration === undefined ? null : (
            <PanneauAmelioration
              tour={etatRun.tourAmelioration}
              definition={definitionAmelioration}
              enCours={etatRun.actionEnCours}
              ameliorationPossible={etatRun.phase === 'prete'}
              onAmeliorer={ameliorerTour}
              onFermer={() => {
                useRunStore.getState().envoyer({ type: 'selection-tour-posee', tour: null });
              }}
            />
          )}
        </div>
        {/*
          En portrait, le plateau 16:9 laisse de la hauteur libre sous lui : le briefing s'y lit
          sans ouvrir le tiroir. Ailleurs (paysage, grand écran), il est masqué et vit dans « Infos ».
        */}
        <BriefingNiveau
          niveau={niveau}
          idTitre="briefing-titre-plateau"
          className="briefing--plateau"
        />
      </main>

      {/*
        Dock du bas : compteurs de la partie, tours disponibles et lancement de la vague. La classe
        `hud` y reste : ce sont les compteurs de la partie (PV, éclats, vague, jokers).
      */}
      <footer className="barre-basse hud" aria-label={t('jeu.hud.titre')}>
        <dl className="hud__grille">
          <CaseHud
            cle="pv"
            etiquette={t('jeu.hud.pv')}
            valeur={formaterNombre(valeurs.pv, i18n.language)}
          />
          <CaseHud
            cle="eclats"
            etiquette={t('jeu.hud.eclats')}
            valeur={formaterNombre(valeurs.eclats, i18n.language)}
          />
          {roundAffiche === null ? null : (
            <CaseHud
              cle="round"
              etiquette={t('jeu.illimite.round')}
              valeur={t('jeu.illimite.valeurRound', {
                numero: formaterNombre(roundAffiche, i18n.language),
              })}
            />
          )}
          <CaseHud
            cle="vague"
            etiquette={illimite ? t('jeu.illimite.vagueDuRound') : t('jeu.hud.vague')}
            valeur={
              illimite
                ? t('jeu.illimite.valeurVagueDuRound', {
                    numero: formaterNombre(vagueDuRoundAffichee ?? 1, i18n.language),
                    total: formaterNombre(vaguesParRound, i18n.language),
                  })
                : t('jeu.hud.valeurVague', {
                    numero: formaterNombre(
                      Math.min(vagueAffichee, niveau.vagues.length),
                      i18n.language,
                    ),
                    total: formaterNombre(niveau.vagues.length, i18n.language),
                  })
            }
          />
          <CaseHud
            cle="jokers"
            etiquette={t('jeu.hud.jokers')}
            valeur={formaterNombre(valeurs.jokers, i18n.language)}
          />
        </dl>
        <div className="barre-basse__tours" role="group" aria-label={t('jeu.tours.titre')}>
          {tours.map((tour) => {
            const nom = t(cleTraductionContenu(tour.nameKey));
            const idCout = `cout-${tour.id}`;
            const selectionnee = etatRun.tourSelectionnee === tour.id;
            const style = {
              '--couleur-element': `var(--element-${tour.element})`,
            } as CSSProperties;
            return (
              <button
                key={tour.id}
                type="button"
                className="bouton-tour"
                style={style}
                data-tour={tour.id}
                aria-pressed={selectionnee}
                aria-label={t('jeu.tours.choisir', { nom })}
                aria-describedby={idCout}
                onClick={() => {
                  useRunStore
                    .getState()
                    .envoyer({ type: 'selection-tour', tourId: selectionnee ? null : tour.id });
                }}
              >
                <IconeElement element={tour.element} />
                <span className="bouton-tour__nom">{nom}</span>
                <span className="bouton-tour__cout" id={idCout}>
                  {t('jeu.tours.cout', { cout: formaterNombre(tour.cout, i18n.language) })}
                </span>
                {selectionnee ? (
                  <span className="bouton-tour__etat">{t('jeu.tours.selectionnee')}</span>
                ) : null}
              </button>
            );
          })}
        </div>
        <div className="barre-basse__lancement">
          <p className="note" id="note-lancement">
            {enCours
              ? t('jeu.vagueEnCours')
              : etatRun.phase === 'choix-carte'
                ? t('jeu.choixCarteEnCours')
                : t('jeu.lancementAide')}
          </p>
          {secondesRestantes === null ? null : (
            <p className="note note--alerte" role="status" data-lancement-auto={secondesRestantes}>
              {t('jeu.illimite.lancementAuto', { count: secondesRestantes })}
            </p>
          )}
          <SelecteurVitesse vitesse={vitesseJeu} onChange={definirVitesseJeu} />
          <Bouton
            variante="principal"
            className="bouton--lancer"
            iconeAvant={<IconeHud compteur="vague" />}
            disabled={!lancementPossible}
            aria-describedby="note-lancement"
            onClick={lancerVague}
          >
            {t('jeu.lancerVague')}
          </Bouton>
          {illimite ? (
            <Bouton
              variante="discret"
              disabled={etatRun.actionEnCours}
              onClick={() => {
                setAbandonDemande(true);
              }}
            >
              {t('jeu.illimite.abandon.bouton')}
            </Bouton>
          ) : null}
        </div>
      </footer>

      {/*
        Fenêtres **au-dessus** du plateau (pop-in) : choix de carte, fin de round et confirmation
        d'abandon. Elles prennent la main tant qu'un choix est attendu.
      */}
      {finDeRound === null ? null : (
        <div className="surcouche" data-surcouche="fin-de-round">
          <div className="surcouche__voile" aria-hidden="true" />
          <PanneauFinDeRound
            roundTermine={finDeRound.roundTermine}
            roundSuivant={finDeRound.roundSuivant}
            versement={finDeRound.versement}
            eclatsRun={finDeRound.eclatsRun}
            onContinuer={() => {
              setFinDeRound(null);
            }}
          />
        </div>
      )}

      {abandonDemande ? (
        <div className="surcouche" data-surcouche="abandon">
          <div className="surcouche__voile" aria-hidden="true" />
          <ConfirmationAbandon
            enCours={etatRun.actionEnCours}
            onConfirmer={abandonnerRun}
            onAnnuler={() => {
              setAbandonDemande(false);
            }}
          />
        </div>
      ) : null}

      {etatRun.optionsCartes === null || finDeRound !== null ? null : (
        <div className="surcouche surcouche--cartes" data-surcouche="cartes">
          <div className="surcouche__voile" aria-hidden="true" />
          <PanneauCartes
            options={etatRun.optionsCartes}
            bonusCardId={etatRun.bonusCardId}
            enCours={etatRun.actionEnCours}
            onChoisir={choisirCarte}
          />
        </div>
      )}

      {panneauInfosOuvert ? (
        <PanneauInfosNiveau niveau={niveau} titre={titreNiveau} onFermer={fermerPanneauInfos} />
      ) : null}

      {/*
        Panneau « Effets de la run » (U-03) : monté au-dessus du plateau, sans pause de la vague.
        Il reçoit l'état d'autorité tel quel — jokers et multiplicateurs ne sont jamais recalculés.
      */}
      {panneauEffetsOuvert ? (
        <PanneauEffetsRun
          run={etatRun.serveur}
          cartes={cartesObtenuesRun}
          vagueEnCours={enCours}
          onFermer={fermerPanneauEffets}
        />
      ) : null}
    </div>
  );
}
